import type { CheckTemplate, DraftQuestionInput } from '@my/check-api';
import { Button } from '@my/ui';
import { ArrowLeft, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { sectionLabelFrom, sectionsInOrder, unitLabel } from './lib/templateTypes';

interface DriverPreviewProps {
  template: CheckTemplate;
  /**
   * Unsaved editor state. The preview renders these rather than the persisted
   * questions, so a manager sees what they are about to publish — not what is
   * already live.
   */
  questions: DraftQuestionInput[];
  onBack: () => void;
}

/**
 * What the driver sees in myDriver, rendered non-interactively so a transport
 * manager can sanity-check a template before publishing.
 *
 * Three things the original mock didn't show, each a real gap it hid:
 *  - An N/A button on rows where the template allows it (equipment that may
 *    not be fitted). Without it a driver has to press OK on a tail-lift that
 *    isn't there.
 *  - The walkaround wrapper — vehicle, odometer, declaration. Not template
 *    content, but what the driver actually completes, and the schema has it
 *    (CheckSessions) while no UI does.
 *  - Type 1 renders without the severity / photo footnote, because Type 1 is
 *    Yes/No only.
 */
export function DriverPreview({ template, questions, onBack }: DriverPreviewProps) {
  const { t } = useTranslation('checks');

  const rows = [...questions].sort((a, b) => a.sortOrder - b.sortOrder);
  const sections = sectionsInOrder(rows);
  const isType2 = template.wacType === 'Type2';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-2">
        <Button variant="ghost" size="xs" leftIcon={ArrowLeft} onClick={onBack}>
          {t('templates.preview.backToEditor')}
        </Button>
        <span className="text-sm font-semibold text-slate-900">
          {t('templates.preview.title', { name: template.name })}
        </span>
        <div className="flex-1" />
        <span className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[10px] font-medium uppercase text-slate-600">
          {unitLabel(template.unitType)}
          {template.wacType
            ? ` · ${t(template.wacType === 'Type1' ? 'templates.wac.type1' : 'templates.wac.type2')}`
            : ''}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto bg-slate-100 py-4">
        <div className="mx-auto w-full max-w-[420px] overflow-hidden rounded-sm border border-slate-300 shadow-sm">
          <header className="bg-slate-800 px-4 py-3">
            {/* Regime-specific: a Pre-Use template is not a walk-around, and
                the driver header saying so would be wrong on a pool car. */}
            <p className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
              {t(
                template.regime === 'Wac'
                  ? 'templates.preview.appBrandWac'
                  : 'templates.preview.appBrandPreUse',
              )}
            </p>
            <p className="text-base font-semibold text-white">{template.name}</p>
            <p className="text-xs text-slate-300">
              {unitLabel(template.unitType)} ·{' '}
              {t('templates.editor.questionCount', { count: rows.length })}
            </p>
          </header>

          <div className="bg-white">
            <div className="border-b border-dashed border-slate-300 bg-slate-50 px-4 py-3">
              <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                {t('templates.preview.wrapperTitle')}
              </p>
              <p className="mt-1 text-[10px] italic text-slate-500">
                {t('templates.preview.wrapperNote')}
              </p>
            </div>

            {sections.map((s) => (
              <div key={s}>
                <p className="bg-white px-4 pb-1 pt-4 text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  {sectionLabelFrom(rows, s)}
                </p>
                {rows
                  .filter((q) => q.sectionKey === s)
                  .map((q) => (
                    <div key={`${q.sectionKey}-${q.sortOrder}`} className="px-4 pb-3">
                      <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="min-w-0 text-sm text-slate-800">
                            {q.label || (
                              <span className="italic text-slate-400">
                                {t('templates.preview.untitled')}
                              </span>
                            )}
                          </p>
                          {q.isMandatory && (
                            <TriangleAlert
                              className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-500"
                              aria-label={t('templates.editor.mandatory')}
                            />
                          )}
                        </div>
                        {/* Pass / Fail / N/A, equal width, 44px minimum so
                            they are hittable with gloves on. N/A takes amber
                            with dark text — white on yellow does not carry
                            enough contrast. */}
                        <div className="mt-2 flex gap-2">
                          <span className="flex min-h-[44px] flex-1 items-center justify-center rounded-sm bg-emerald-600 px-2 text-center text-sm font-semibold text-white">
                            {t('templates.preview.pass')}
                          </span>
                          <span className="flex min-h-[44px] flex-1 items-center justify-center rounded-sm bg-red-600 px-2 text-center text-sm font-semibold text-white">
                            {t('templates.preview.fail')}
                          </span>
                          {q.allowNotApplicable && (
                            <span className="flex min-h-[44px] flex-1 items-center justify-center rounded-sm bg-amber-400 px-2 text-center text-sm font-semibold text-slate-900">
                              {t('templates.preview.na')}
                            </span>
                          )}
                        </div>
                        {isType2 && (
                          <p className="mt-1.5 text-[10px] text-slate-500">
                            {t('templates.preview.type2Footnote', {
                              severity: q.defaultSeverity,
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            ))}

            <div className="border-t border-dashed border-slate-300 bg-slate-50 px-4 py-3">
              <p className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                {t('templates.preview.declarationTitle')}
              </p>
              <p className="mt-1 text-[10px] italic text-slate-500">
                {t('templates.preview.declarationNote')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
